import React, { useEffect, useState } from "react";
import { ListOrdered, CheckCircle2, Circle, Award, Trash2 } from "lucide-react";
import CertificateModal from "./CertificateModal";

/**
 * Right-rail for long sections:
 *  - "On this page" anchor menu (smooth scroll)
 *  - For section.id === "health-safety": acknowledgement checkboxes per subsection
 *    + Generate completion certificate button
 *
 * Acks are managed by parent (Section.jsx); we just render + call back.
 */
export default function SectionRail({ sectionId, sectionTitle, headings, acks, onToggle, onReset }) {
    const isTrackable = sectionId === "health-safety";
    const [activeSlug, setActiveSlug] = useState(null);
    const [certOpen, setCertOpen] = useState(false);

    useEffect(() => {
        const obs = new IntersectionObserver(
            (entries) => {
                const visible = entries
                    .filter(e => e.isIntersecting)
                    .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
                if (visible[0]) setActiveSlug(visible[0].target.id);
            },
            { rootMargin: "-25% 0px -65% 0px", threshold: 0 }
        );
        headings.forEach(h => {
            const el = document.getElementById(h.slug);
            if (el) obs.observe(el);
        });
        return () => obs.disconnect();
    }, [headings]);

    const items = headings.filter(h => h.level === 2);
    if (items.length < 3) return null;

    const completed = items.filter(h => acks[h.slug]).length;
    const pct = Math.round((completed / items.length) * 100);

    return (
        <aside className="hidden xl:block w-72 flex-shrink-0 print:hidden" data-testid="section-rail">
            <div className="sticky top-28 pl-8 pr-4 py-2">
                <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-gray-500 font-semibold mb-3">
                    <ListOrdered className="h-3 w-3" />
                    On this page
                </div>

                <nav className="space-y-1 mb-6">
                    {items.map(h => {
                        const checked = !!acks[h.slug];
                        const isActive = activeSlug === h.slug;
                        return (
                            <div key={h.slug} className="flex items-start gap-2 group">
                                {isTrackable && (
                                    <button
                                        onClick={() => onToggle(h.slug)}
                                        className="mt-1.5 text-gray-400 hover:text-[#2C7A85] transition-colors flex-shrink-0"
                                        aria-label={checked ? "Mark as unread" : "Mark as read"}
                                        data-testid={`ack-${h.slug}`}
                                    >
                                        {checked
                                            ? <CheckCircle2 className="h-4 w-4 text-[#2C7A85]" />
                                            : <Circle className="h-4 w-4" />}
                                    </button>
                                )}
                                <a
                                    href={`#${h.slug}`}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        const el = document.getElementById(h.slug);
                                        if (el) {
                                            el.scrollIntoView({ behavior: "smooth", block: "start" });
                                            window.history.replaceState(null, "", `#${h.slug}`);
                                        }
                                    }}
                                    className={`block text-[13px] leading-snug py-1 transition-colors ${
                                        isActive ? "text-[#2C7A85] font-medium" : "text-gray-600 hover:text-gray-900"
                                    } ${checked && isTrackable ? "line-through decoration-[#2C7A85]/40" : ""}`}
                                    data-testid={`anchor-${h.slug}`}
                                >
                                    {h.text}
                                </a>
                            </div>
                        );
                    })}
                </nav>

                {isTrackable && (
                    <div className="border-t border-gray-200 pt-5">
                        <div className="flex items-center justify-between mb-2">
                            <div className="text-[11px] uppercase tracking-widest text-gray-500 font-semibold">Progress</div>
                            <div className="text-[11px] text-gray-500">{completed} / {items.length}</div>
                        </div>
                        <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden mb-4">
                            <div
                                className="h-full bg-[#2C7A85] transition-all duration-300"
                                style={{ width: `${pct}%` }}
                                data-testid="ack-progress-bar"
                            />
                        </div>
                        <button
                            onClick={() => setCertOpen(true)}
                            disabled={completed === 0}
                            className="w-full inline-flex items-center justify-center gap-2 bg-[#2C7A85] hover:bg-[#1F5F69] disabled:opacity-40 disabled:cursor-not-allowed text-white text-sm font-medium px-3 py-2.5 rounded-md transition-colors mb-2"
                            data-testid="open-certificate"
                        >
                            <Award className="h-4 w-4" />
                            Completion Certificate
                        </button>
                        {completed > 0 && (
                            <button
                                onClick={onReset}
                                className="w-full inline-flex items-center justify-center gap-1.5 text-xs text-gray-500 hover:text-[#B91C1C] px-2 py-1.5 rounded transition-colors"
                                data-testid="clear-acks"
                            >
                                <Trash2 className="h-3 w-3" /> Reset progress
                            </button>
                        )}
                    </div>
                )}
            </div>

            <CertificateModal
                open={certOpen}
                onClose={() => setCertOpen(false)}
                sectionId={sectionId}
                sectionTitle={sectionTitle}
                items={items}
                acks={acks}
            />
        </aside>
    );
}
